import httpclient from 'libs/Axios';
import httpClientWithoutAbort from 'libs/Axios/httpClientWithoutAbort';

export type ReelCopyPayload = {
  content_type: 'REEL';
  category: string;
  sub_category: string;
  title: string;
};

export type ReelStylesPayload = {
  title: string;
  description: string;
  category: string;
  sub_category: string;
};

export type GenerateReelPayload = ReelStylesPayload & {
  source: 'REEL';
  style: string;
  images: string[];
};

export type ReelGenerationResult = {
  reel_id: number;
  video_id: string;
  video_status: string;
};

export type ReelGenerationStatus = {
  video_id: string;
  status: string;
  video_url: string | null;
};

export type SocialHubPlatformStatus = {
  status: boolean;
  info?: Record<string, any>;
};

export type SocialHubStatus = {
  fb?: SocialHubPlatformStatus;
  ig?: SocialHubPlatformStatus;
  yt?: SocialHubPlatformStatus;
  gb?: SocialHubPlatformStatus;
  wa?: SocialHubPlatformStatus;
};

const responseData = <T>(response: any): T => response?.data?.data as T;

export const reelsApi = {
  async getSocialHubStatus(): Promise<SocialHubStatus> {
    const response = await httpclient().get('/comm/socialHub/status');
    return responseData<SocialHubStatus>(response) || {};
  },

  async generateDescription(payload: ReelCopyPayload): Promise<string> {
    const response = await httpclient().post(
      '/master/generate/socialMediaDescription',
      payload
    );
    return responseData<{ description?: string }>(response)?.description || '';
  },

  async generateHashtags(payload: ReelCopyPayload): Promise<string[]> {
    const response = await httpclient().post('/master/generate/hashtags', payload);
    const hashtags = responseData<{ hashtags?: string[] }>(response)?.hashtags;
    return Array.isArray(hashtags) ? hashtags.filter(Boolean) : [];
  },

  async getStyles(payload: ReelStylesPayload): Promise<string[]> {
    const response = await httpclient().post('/master/reels/styles', payload);
    const styles = responseData<{ styles?: string[] }>(response)?.styles;
    return Array.isArray(styles) ? styles.filter(Boolean) : [];
  },

  async generate(payload: GenerateReelPayload): Promise<ReelGenerationResult> {
    // Reel generation can take long enough for the user to leave this route.
    // This client is intentionally excluded from route-change cancellation.
    const response = await httpClientWithoutAbort().post(
      '/master/reels/generate',
      payload
    );
    return responseData<ReelGenerationResult>(response);
  },

  async getStatus(videoId: string): Promise<ReelGenerationStatus> {
    const response = await httpclient().get(
      `/master/reels/status?video_id=${encodeURIComponent(videoId)}`
    );
    return responseData<ReelGenerationStatus>(response);
  },

  list(pageIndex = 1, pageSize = 10) {
    return httpclient().get(
      `/master/reels/list?page_index=${pageIndex}&page_size=${pageSize}&sort_by=REEL_ID&sort_type=desc`
    );
  },

  view(reelId: number) {
    return httpclient().get(
      `/master/reels/view?reel_id=${encodeURIComponent(String(reelId))}`
    );
  },

  async updatePlatform(reelId: number, platform: string) {
    const response = await httpclient().post(
      `/master/reels/updatePlatform/${encodeURIComponent(String(reelId))}`,
      { platform }
    );
    return responseData<Record<string, any>>(response);
  },

  delete(reelId: number) {
    return httpclient().delete(
      `/master/reels/delete?reel_id=${encodeURIComponent(String(reelId))}`
    );
  },
};
